import { names } from './names';

export const keyToString = (key) => (isNaN(Number(key)) ? key : `${key}str`);

const buildPath = (...segments) =>
  segments.filter((segment) => segment !== undefined).join('.');

// DESK
const baseDeskPathBuilder = (...segments) =>
  buildPath(names.main.desks, ...segments);

const deskPathBuilders = {
  byIndex: (index) => baseDeskPathBuilder(index),
  name: (index) => baseDeskPathBuilder(index, names.desk.name),
  uniqueDateRange: (index) =>
    baseDeskPathBuilder(index, names.desk.uniqueDateRange),
  dateRanges: (index) => baseDeskPathBuilder(index, names.desk.dateRanges),
  workingDays: (index) => baseDeskPathBuilder(index, names.desk.workingDays),
  isHoliday: (index) => baseDeskPathBuilder(index, names.desk.isHoliday),
  scheduleType: (index) => baseDeskPathBuilder(index, names.desk.scheduleType),
  intervalType: (index) => baseDeskPathBuilder(index, names.desk.intervalType),
  scheduleSettings: (index) =>
    baseDeskPathBuilder(index, names.desk.scheduleSettings),
  isCommonSchedules: (index) =>
    baseDeskPathBuilder(index, names.desk.isCommonSchedules),
};

// DESK DATE RANGE
const baseDeskDateRangePathBuilder = (deskIndex, ...segments) =>
  buildPath(names.main.desks, deskIndex, names.desk.dateRanges, ...segments);

const deskDateRangePathBuilders = {
  byIndex: (deskIndex, rangeIndex) =>
    baseDeskDateRangePathBuilder(deskIndex, rangeIndex),
  fromDate: (deskIndex, rangeIndex) =>
    baseDeskDateRangePathBuilder(
      deskIndex,
      rangeIndex,
      names.deskDateRange.fromDate
    ),
  toDate: (deskIndex, rangeIndex) =>
    baseDeskDateRangePathBuilder(
      deskIndex,
      rangeIndex,
      names.deskDateRange.toDate
    ),
};

// DESK SCHEDULE SETTING
const baseDeskScheduleSettingPathBuilder = (
  deskIndex,
  entityKey,
  ...segments
) =>
  buildPath(
    names.main.desks,
    deskIndex,
    names.desk.scheduleSettings,
    keyToString(entityKey),
    ...segments
  );

const deskScheduleSettingPathBuilders = {
  byEntity: (deskIndex, entityKey) =>
    baseDeskScheduleSettingPathBuilder(deskIndex, entityKey),
  intervalsPerDay: (deskIndex, entityKey) =>
    baseDeskScheduleSettingPathBuilder(
      deskIndex,
      entityKey,
      names.deskScheduleSetting.intervalsPerDay
    ),
  ignoreIntervalsPerDay: (deskIndex, entityKey) =>
    baseDeskScheduleSettingPathBuilder(
      deskIndex,
      entityKey,
      names.deskScheduleSetting.ignoreIntervalsPerDay
    ),
  intervals: (deskIndex, entityKey) =>
    baseDeskScheduleSettingPathBuilder(
      deskIndex,
      entityKey,
      names.deskScheduleSetting.intervals
    ),
  start: (deskIndex, entityKey) =>
    baseDeskScheduleSettingPathBuilder(
      deskIndex,
      entityKey,
      names.deskScheduleSetting.start
    ),
  end: (deskIndex, entityKey) =>
    baseDeskScheduleSettingPathBuilder(
      deskIndex,
      entityKey,
      names.deskScheduleSetting.end
    ),
  duration: (deskIndex, entityKey) =>
    baseDeskScheduleSettingPathBuilder(
      deskIndex,
      entityKey,
      names.deskScheduleSetting.duration
    ),
};

// SCHEDULE SETTING INTERVAL
const baseScheduleSettingIntervalPathBuilder = (
  deskIndex,
  entityKey,
  ...segments
) =>
  buildPath(
    names.main.desks,
    deskIndex,
    names.desk.scheduleSettings,
    keyToString(entityKey),
    names.deskScheduleSetting.intervals,
    ...segments
  );

const scheduleSettingIntervalPathBuilders = {
  byIndex: (deskIndex, entityKey, intervalIndex) =>
    baseScheduleSettingIntervalPathBuilder(deskIndex, entityKey, intervalIndex),
  active: (deskIndex, entityKey, intervalIndex) =>
    baseScheduleSettingIntervalPathBuilder(
      deskIndex,
      entityKey,
      intervalIndex,
      names.scheduleSettingInterval.active
    ),
  intervalId: (deskIndex, entityKey, intervalIndex) =>
    baseScheduleSettingIntervalPathBuilder(
      deskIndex,
      entityKey,
      intervalIndex,
      names.scheduleSettingInterval.intervalId
    ),
  mode: (deskIndex, entityKey, intervalIndex) =>
    baseScheduleSettingIntervalPathBuilder(
      deskIndex,
      entityKey,
      intervalIndex,
      names.scheduleSettingInterval.mode
    ),
  availability: (deskIndex, entityKey, intervalIndex) =>
    baseScheduleSettingIntervalPathBuilder(
      deskIndex,
      entityKey,
      intervalIndex,
      names.scheduleSettingInterval.availability
    ),
  duration: (deskIndex, entityKey, intervalIndex) =>
    baseScheduleSettingIntervalPathBuilder(
      deskIndex,
      entityKey,
      intervalIndex,
      names.scheduleSettingInterval.duration
    ),
};

// NOTIFICATION
const baseNotificationPathBuilder = (...segments) =>
  buildPath(names.main.notifications, ...segments);

const notificationPathBuilders = {
  byIndex: (index) => baseNotificationPathBuilder(index),
  type: (index) => baseNotificationPathBuilder(index, names.notification.type),
  title: (index) =>
    baseNotificationPathBuilder(index, names.notification.title),
  channels: (index) =>
    baseNotificationPathBuilder(index, names.notification.channels),
  fromDateTime: (index) =>
    baseNotificationPathBuilder(index, names.notification.fromDateTime),
  intervals: (index) =>
    baseNotificationPathBuilder(index, names.notification.intervals),
  isCommonMessage: (index) =>
    baseNotificationPathBuilder(index, names.notification.isCommonMessage),
  message: (index) =>
    baseNotificationPathBuilder(index, names.notification.message),
  proxyMessage: (index) =>
    baseNotificationPathBuilder(index, names.notification.proxyMessage),
  intervalMessages: (index) =>
    baseNotificationPathBuilder(index, names.notification.intervalMessages),
};

// NOTIFICATION INTERVAL MESSAGE
const baseNotificationIntervalMessagePathBuilder = (
  notificationIndex,
  entityKey,
  ...segments
) =>
  buildPath(
    names.main.notifications,
    notificationIndex,
    names.notification.intervalMessages,
    keyToString(entityKey),
    ...segments
  );

const notificationIntervalMessagePathBuilders = {
  byEntity: (notificationIndex, entityKey) =>
    baseNotificationIntervalMessagePathBuilder(notificationIndex, entityKey),
  message: (notificationIndex, entityKey) =>
    baseNotificationIntervalMessagePathBuilder(
      notificationIndex,
      entityKey,
      names.notificationIntervalMessage.message
    ),
  proxyMessage: (notificationIndex, entityKey) =>
    baseNotificationIntervalMessagePathBuilder(
      notificationIndex,
      entityKey,
      names.notificationIntervalMessage.proxyMessage
    ),
};

export const fieldPathBuilders = {
  desk: deskPathBuilders,
  deskDateRange: deskDateRangePathBuilders,
  deskScheduleSetting: deskScheduleSettingPathBuilders,
  scheduleSettingInterval: scheduleSettingIntervalPathBuilders,
  notification: notificationPathBuilders,
  notificationIntervalMessage: notificationIntervalMessagePathBuilders,
};
